/**
 * Telnyx WebRTC bridge — used ONLY for phone numbers with provider "telnyx"
 * (Canadian numbers). The Twilio Voice SDK path in VoiceContext is untouched;
 * this module adapts Telnyx calls to the same call interface the app expects
 * (accept / reject / disconnect / mute / sendDigits / on(event)).
 */
import { TelnyxRTC } from "@telnyx/webrtc";
import axios from "axios";
import { API } from "../App";

let client = null;
let connecting = null;
const shims = new Map();

export class TelnyxCallShim {
  constructor(call) {
    this._call = call;
    this._cbs = {};
    this._active = false;
    this._ended = false;
    this.provider = "telnyx";
    const opts = call.options || {};
    this.parameters = {
      CallSid: call.id || "",
      From: opts.remoteCallerNumber || opts.callerNumber || "",
      To: opts.destinationNumber || "",
    };
    this.customParameters = new Map();
  }
  on(ev, cb) { (this._cbs[ev] = this._cbs[ev] || []).push(cb); }
  _emit(ev) { (this._cbs[ev] || []).forEach((cb) => { try { cb(); } catch { /* ignore */ } }); }
  accept() { try { this._call.answer(); } catch { /* ignore */ } }
  reject() { try { this._call.hangup(); } catch { /* ignore */ } }
  disconnect() { try { this._call.hangup(); } catch { /* ignore */ } }
  mute(m) { try { m ? this._call.muteAudio() : this._call.unmuteAudio(); } catch { /* ignore */ } }
  sendDigits(d) { try { this._call.dtmf(d); } catch { /* ignore */ } }
  _update(state) {
    if (this._ended) return;
    if (state === "active") {
      if (!this._active) { this._active = true; this._emit("accept"); }
    } else if (state === "ringing") {
      this._emit("ringing");
    } else if (state === "hangup" || state === "destroy") {
      this._ended = true;
      this._emit(this._active ? "disconnect" : "cancel");
    }
  }
}

const ensureAudioSink = () => {
  let el = document.getElementById("telnyx-remote-audio");
  if (!el) {
    el = document.createElement("audio");
    el.id = "telnyx-remote-audio";
    el.autoplay = true;
    el.style.display = "none";
    document.body.appendChild(el);
  }
};

export const isTelnyxReady = () => !!(client && client.connected);

export const initTelnyxClient = async ({ onIncoming, onReady, onError } = {}) => {
  if (client) return client;
  if (connecting) return connecting;
  connecting = (async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(`${API}/voice/telnyx-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ensureAudioSink();
    const c = new TelnyxRTC({ login_token: res.data.login_token });
    c.remoteElement = "telnyx-remote-audio";
    c.on("telnyx.ready", () => { onReady && onReady(); });
    c.on("telnyx.error", (e) => { onError && onError(e); });
    c.on("telnyx.socket.close", () => {
      try { c.disconnect(); } catch { /* ignore */ }
      client = null;
      connecting = null;
    });
    c.on("telnyx.notification", (n) => {
      if (n.type !== "callUpdate" || !n.call) return;
      const call = n.call;
      let shim = shims.get(call.id);
      if (!shim) {
        shim = new TelnyxCallShim(call);
        shims.set(call.id, shim);
        if (call.direction === "inbound") {
          if (!shim.parameters.From) {
            shim.parameters.From = (call.options && call.options.remoteCallerNumber) || "Unknown";
          }
          onIncoming && onIncoming(shim);
        }
      }
      shim._call = call;
      shim._update(call.state);
      if (call.state === "destroy") shims.delete(call.id);
    });
    await c.connect();
    client = c;
    connecting = null;
    return c;
  })();
  try {
    return await connecting;
  } catch (e) {
    connecting = null;
    throw e;
  }
};

export const telnyxNewCall = (destinationNumber, callerNumber) => {
  if (!client) throw new Error("Telnyx client not connected");
  const call = client.newCall({
    destinationNumber,
    callerNumber,
    audio: true,
    video: false,
  });
  const shim = new TelnyxCallShim(call);
  shims.set(call.id, shim);
  return shim;
};

export const destroyTelnyxClient = () => {
  try { client && client.disconnect(); } catch { /* ignore */ }
  client = null;
  connecting = null;
  shims.clear();
};
